/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import axios from 'axios'
import store from './store/store.js'

const axiosInstance = axios.create({
  withCredentials: true,
  baseURL: process.env.VUE_APP_BASE_URL
})

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    // Redirect to dashboard if user tries to enter /.
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/dashboard',
          name: 'dashboard',
          meta: { requiresAuth: true },
          component: () => import('./views/Dashboard.vue')
        },
        {
          path: '/profile',
          name: 'profile',
          meta: { requiresAuth: true },
          component: () => import('./views/profile/Profile.vue')
        },
        {
          path: '/create',
          name: 'create',
          meta: { requiresAuth: true },
          component: () => import('./views/Create.vue')
        },
        {
          path: '/track',
          name: 'track',
          meta: { requiresAuth: true },
          component: () => import('./views/track/Track.vue'),
          children: [
            {
              path: '/',
              meta: { requiresAuth: true },
              component: () => import('./views/track/TrackList.vue')
            },
            {
              path: 'details/:id',
              meta: {
                requiresAuth: true,
                breadcrumb: [
                  { title: 'Dashboard', url: '/' },
                  { title: 'Track', url: '/track' },
                  { title: 'Simulation Detail', active: true }
                ]
              },
              component: () => import('./views/track/TrackDetail.vue')
            }
          ]
        },
        {
          path: '/packages',
          name: 'packages',
          meta: { requiresAuth: true },
          component: () => import('./views/packages/Package.vue'),
          children: [
            {
              path: '/',
              meta: { requiresAuth: true },
              component: () => import('./views/packages/Packages.vue')
            },
            {
              path: 'checkout',
              meta: {
                requiresAuth: true, breadcrumb: [
                  { title: 'Dashboard', url: '/' },
                  { title: 'Packages', url: '/packages' },
                  { title: 'Package Checkout', active: true }
                ],
              },
              component: () => import('./views/packages/PackageCheckout.vue')
            }
          ]
        },
        {
          path: '/transactions',
          name: 'transactions',
          meta: { requiresAuth: true },
          component: () => import('./views/Transactions.vue')
        },
        {
          path: '/tickets',
          name: 'tickets',
          meta: { requiresAuth: true },
          component: () => import('./views/tickets/Ticket.vue'),
          children: [
            {
              path: '/',
              meta: { requiresAuth: true },
              component: () => import('./views/tickets/TicketList.vue')
            },
            {
              path: 'details/:id',
              meta: {
                requiresAuth: true,
                breadcrumb: [
                  { title: 'Dashboard', url: '/' },
                  { title: 'Tickets', url: '/tickets' },
                  { title: 'Ticket Detail', active: true }
                ]
              },
              component: () => import('./views/tickets/TicketDetail.vue')
            },
            {
              path: 'new',
              meta: { requiresAuth: true },
              component: () => import('./views/tickets/NewTicket.vue')
            }
          ]
        },
        {
          path: '/blue-team',
          name: 'blue-team',
          meta: { requiresBlueTeamAccess: true},
          component: () => import('./views/blue-team/BlueTeam.vue')
        },
        {
          path: '/blue-team/details/:id',
          name: 'blue-team-detail',
          meta: { 
            requiresBlueTeamAccess: true, 
            breadcrumb: [
            { title: 'Dashboard', url: '/' },
            { title: 'Blue Team', url: '/blue-team' },
            { title: 'Blue Team Detail', active: true }
          ]},
          component: () => import('./views/blue-team/BlueTeamDetail.vue')
        },
        {
          path: 'about-us',
          name: 'about-us',
          meta: {requiresAuth: true},
          component: () => import('./views/pages/AboutUs.vue')
        },
        {
          path: 'privacy-policy',
          name: 'privacy-policy',
          meta: {requiresAuth: true},
          component: () => import('./views/pages/PrivacyPolicy.vue')
        },
        {
          path: 'distance-sales-agreement',
          name: 'distance-sales-agreement',
          meta: {requiresAuth: true},
          component: () => import('./views/pages/DistanceSalesAgreement.vue')
        },
        {
          path: 'cookie-policy',
          name: 'cookie-policy',
          meta: {requiresAuth: true},
          component: () => import('./views/pages/CookiePolicy.vue')
        },
      ]
    },
    {
      // =============================================================================
      // Legal
      // =============================================================================
      path: '/legal',
      component: () => import('./layouts/legal/Legal.vue'),
      children: [
        {
          path: 'about-us',
          name: 'about-us',
          component: () => import('./views/pages/AboutUs.vue')
        },
        {
          path: 'privacy-policy',
          name: 'privacy-policy',
          component: () => import('./views/pages/PrivacyPolicy.vue')
        },
        {
          path: 'distance-sales-agreement',
          name: 'distance-sales-agreement',
          component: () => import('./views/pages/DistanceSalesAgreement.vue')
        },
        {
          path: 'cookie-policy',
          name: 'cookie-policy',
          component: () => import('./views/pages/CookiePolicy.vue')
        },
      ]
    },
    {
      path: '/management',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: 'users',
          name: 'users',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/Users.vue')
        },
        {
          path: 'packages',
          name: 'packages',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/Packages.vue')
        },
        {
          path: 'operating-systems',
          name: 'operating-systems',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/OperatingSystems.vue')
        },
        {
          path: 'languages',
          name: 'languages',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/Languages.vue')
        },
        {
          path: 'protections',
          name: 'protections',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/Protections.vue')
        },
        {
          path: 'attacks',
          name: 'attacks',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/Attacks.vue')
        },
        {
          path: 'rules',
          name: 'rules',
          meta: { requiresAuth: true, requiresAdmin: true },
          component: () => import('./views/management/Rules.vue')
        },
        {
          path: 'rules/create',
          name: 'create-rule',
          meta: { requiresAuth: true, requiresAdmin: true},
          component: () => import('./views/management/CreateRule.vue')
        },
        {
          path: 'rules/edit/:id',
          name: 'edit-rule',
          meta: { requiresAuth: true, requiresAdmin: true},
          component: () => import('./views/management/EditRule.vue')
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/reset-password',
          name: 'reset-password',
          component: () => import('@/views/pages/ResetPassword.vue')
        },
        {
          path: '/reset-password/:id',
          name: 'reset-password-id',
          component: () => import('@/views/pages/ResetPasswordWithId.vue')
        },
        {
          path: '/login',
          name: 'login',
          component: () => import('@/views/pages/Login.vue')
        },
        {
          path: '/register',
          name: 'register',
          component: () => import('@/views/pages/Register.vue')
        },
        {
          path: '/pricing',
          name: '/pricing',
          component: () => import('@/views/pages/Pricing.vue')
        },
        {
          path: '/404',
          name: '404',
          meta: { requiresAuth: true },
          component: () => import('@/views/pages/Error404.vue')
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/404'
    }
  ]
})

router.beforeEach((to, from, next) => {
  axiosInstance.get('/auth/status')
    .then(res => {
      if (res.data.status === true) {
        if(to.matched.some(record => record.meta.requiresBlueTeamAccess)) {
          if(res.data.user && res.data.user.packageType && res.data.user.packageType.blueTeam) {
            store.commit('UPDATE_USER_INFO', res.data.user)
            next();
          } else {
            next('/dashboard');
          }
        } else {
          if (to.matched.some(record => record.meta.requiresAuth)) {
            store.commit('UPDATE_USER_INFO', res.data.user)
            next()
          } else {
            next('/dashboard');
          }
        }
      } else {
        if (to.matched.some(record => record.meta.requiresAuth)) {
          next('/login')
        } else {
          next();
        }
      }
    })
    .catch(err => {
      console.log('Error occurred', err.response)
      next('/login')
    })
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
